import { useSelector } from 'react-redux';
import { useMemo } from 'react';
import { IStore } from '../interfaces/StoreTypes';
import { getCountryPPVEvents } from '../utils/user';
import { checkShowLockIcon } from '../helpers/vod';
import { get } from '@intigral/utils/lib/browser-storage/localStorage';
import { STORE_NAME } from '@intigral/constant';
import f2 from '../helpers/formatter2';
import { ButtonTypes } from '../config/constants';
import user from '../helpers/user';
import { useCountryCode, useConfigurationSections } from './useConfiguration';
import { usePPVEvents } from './usePPVEvents';

/**
 * Custom hook for managing all PPV-related functionality
 * Centralizes PPV detection, event management, and lock status logic
 */
const usePPVManagement = () => {
    const userChannels = useSelector((state: IStore) => state.liveTv.userChannels.channels) ?? [];
    // Using React Query instead of Redux for configuration
    const { paidVsFree, operators } = useConfigurationSections();
    const enableLockIcon = paidVsFree?.lockIconEnable || false;
    //  FIXED: Using React Query-style hook instead of Redux generalConfiguration
    const { data: ppvEventConfig } = usePPVEvents();
    const countryCode = useCountryCode();

    // Get user's PPV subscription list
    const userPpvList = useMemo(() => {
        const userInfo = get(STORE_NAME.USER);
        return userInfo?.ppvList || [];
    }, []);

    /**
     * Detects if a channel is a PPV channel
     * @param channel - Channel object from userChannels
     * @param item - Item object from search results or other sources
     * @returns boolean - true if channel is PPV
     */
    const detectChannelPPV = useMemo(
        () =>
            (channel: any, item: any = null) => {
                // First check explicit isPPV flags
                if (item?.isPPV || channel?.isPPV) {
                    return true;
                }

                // Check if channel has subscriptions with PPV in the title
                const subscriptions = channel?.subscriptions || [];
                const hasPPVSubscription = subscriptions.some((sub: any) => {
                    const subscriptionTitle = (sub?.subscriptionTitle || '').toLowerCase();
                    return subscriptionTitle.includes('ppv');
                });

                // If channel has PPV subscription, it's likely a PPV channel
                // Even if PPV config is not loaded yet, we should treat it as PPV
                if (hasPPVSubscription) {
                    // If PPV config is available, verify with events
                    if (ppvEventConfig && operators) {
                        const ppvEvents = getCountryPPVEvents(ppvEventConfig, operators, countryCode);
                        const channelSubscriptionGuids = subscriptions.map((sub: any) => sub.subscriptionGUID) || [];

                        const hasAvailablePPVEvent = ppvEvents.some((event: any) =>
                            channelSubscriptionGuids.includes(event.subscriptionGuid)
                        );

                        return hasAvailablePPVEvent;
                    }

                    // If PPV config is not available yet, assume it's PPV if it has PPV subscription
                    return true;
                }

                return false;
            },
        [ppvEventConfig, operators]
    );

    /**
     * Gets the PPV event for a specific channel
     * @param channel - Channel object with isPPV and subscriptions
     * @returns PPV event object or null
     */
    const getPPVEventForChannel = useMemo(
        () => (channel: any) => {
            if (!channel?.isPPV || !ppvEventConfig || !operators) {
                return null;
            }

            const ppvEvents = getCountryPPVEvents(ppvEventConfig, operators, countryCode);
            const channelSubscriptionGuids = channel?.subscriptions?.map((sub: any) => sub.subscriptionGUID) || [];

            // Find the PPV event that matches this channel's subscription
            const ppvEvent = ppvEvents.find((event: any) => channelSubscriptionGuids.includes(event.subscriptionGuid));

            return ppvEvent || null;
        },
        [ppvEventConfig, operators]
    );

    /**
     * Checks if a PPV event is purchased by the user
     * @param ppvEvent - PPV event object
     * @returns boolean - true if purchased
     */
    const isPPVEventPurchased = useMemo(
        () => (ppvEvent: any) => {
            if (!ppvEvent) return false;

            return userPpvList.some((userPpv: any) => userPpv.eventId === ppvEvent.eventId);
        },
        [userPpvList]
    );

    /**
     * Detects if an item is a PPV item
     * @param item - Item object
     * @param buttonType - Button type for context
     * @returns boolean - true if item is PPV
     */
    const isPPVItem = useMemo(
        () => (item: any, buttonType?: string) => {
            return (
                item?.isPPV ||
                (item?.subscriptionGuids &&
                    item.subscriptionGuids.some((guid: string) => guid.toLowerCase().includes('ppv'))) ||
                (buttonType === ButtonTypes.RewindItem && item?.category === 'LINEAR')
            );
        },
        []
    );

    /**
     * Determines if lock should be shown for a channel/item
     * @param item - Item object
     * @param channelId - Optional channel ID for lookup
     * @returns object with isLocked status and enhanced channel
     */
    const shouldShowLock = useMemo(
        () => (item: any, channelId?: string) => {
            if (!channelId) {
                return {
                    isLocked: checkShowLockIcon(item?.subscriptionGuids ?? []) && enableLockIcon,
                    enhancedChannel: null,
                };
            }

            const actualChannelId = channelId || item?.channelID || item?.channelId || item?.id;
            const channelDetails: any = userChannels.find((el) => el.channelID == actualChannelId) ?? {};

            // Create enhanced channel with proper PPV detection
            const enhancedChannel = {
                ...channelDetails,
                isPPV: detectChannelPPV(channelDetails, item),
            };

            const subscriptionGuids = enhancedChannel?.subscriptions
                ? f2.pluck(enhancedChannel?.subscriptions, 'subscriptionGUID')
                : [];

            const isLocked = checkShowLockIcon(subscriptionGuids ?? [], enhancedChannel) && enableLockIcon;

            return {
                isLocked,
                enhancedChannel,
            };
        },
        [userChannels, enableLockIcon, detectChannelPPV]
    );

    /**
     * Gets enhanced channel information for a given item
     * @param item - Item object
     * @param channelId - Optional channel ID for lookup
     * @returns Enhanced channel object with PPV information
     */
    const getEnhancedChannel = useMemo(
        () => (item: any, channelId?: string) => {
            const actualChannelId =
                channelId || item?.channelId || item?.channelID || item?.id || item?.channelNo || item?.channelNumber;

            let channelDetails =
                userChannels.find(
                    (el) =>
                        el.channelID == actualChannelId ||
                        el.channelId == actualChannelId ||
                        el.id == actualChannelId ||
                        el.channelNumber == actualChannelId ||
                        el.channelNo == actualChannelId
                ) ?? {};

            // If no channel found by ID, try finding by channel name
            if (!channelDetails.channelID && item?.channelName) {
                channelDetails =
                    userChannels.find(
                        (el) =>
                            el.channelName === item.channelName ||
                            el.name === item.channelName ||
                            el.title === item.channelName
                    ) ?? {};
            }

            // Create enhanced channel with PPV information
            return {
                ...channelDetails,
                isPPV: detectChannelPPV(channelDetails, item),
                subscriptions: channelDetails?.subscriptions || [],
            };
        },
        [userChannels, detectChannelPPV]
    );

    /**
     * Comprehensive PPV status check for an item
     * @param item - Item object
     * @param channelId - Optional channel ID
     * @returns Complete PPV status information
     */
    const getPPVStatus = useMemo(
        () => (item: any, channelId?: string) => {
            const enhancedChannel = getEnhancedChannel(item, channelId);
            const ppvEvent = getPPVEventForChannel(enhancedChannel);
            const isPurchased = isPPVEventPurchased(ppvEvent);
            const { isLocked } = shouldShowLock(item, channelId);

            return {
                isPPV: enhancedChannel.isPPV,
                ppvEvent,
                isPurchased,
                isLocked,
                enhancedChannel,
                shouldShowPPVModal: enhancedChannel.isPPV && !isPurchased,
            };
        },
        [getEnhancedChannel, getPPVEventForChannel, isPPVEventPurchased, shouldShowLock]
    );

    /**
     * Filters channels to hide PPV channels without available events for the current payment method
     * This ensures consistent behavior between EPG and channel search
     * @param channels - Array of channels to filter
     * @param enableLogging - Whether to log filtering actions (default: false)
     * @returns Filtered array with inaccessible PPV channels removed
     */
    const filterChannelsForPPVAccess = useMemo(
        () =>
            (channels: any[], enableLogging = false) => {
                if (!Array.isArray(channels)) return [];

                const filteredChannels = channels.filter((channel: any) => {
                    // Check if this is a PPV channel by isPPV flag OR subscription patterns
                    const isPPVByFlag = channel?.isPPV;
                    const isPPVBySubscription = channel?.subscriptions?.some((sub: any) => {
                        const subscriptionGUID = sub.subscriptionGUID?.toLowerCase() || '';
                        const subscriptionTitle = sub.subscriptionTitle?.toLowerCase() || '';
                        return subscriptionGUID.includes('ppv') || subscriptionTitle.includes('ppv');
                    });

                    const isActuallyPPV = isPPVByFlag || isPPVBySubscription;

                    // If not a PPV channel, always show it
                    if (!isActuallyPPV) return true;

                    // If PPV config is not loaded yet, show the channel (it will be filtered later)
                    if (!ppvEventConfig || !operators) return true;

                    // Get user operator and PPV settings
                    const userOperator = user.getUserInfo()?.operator;
                    const ppvEnabled = operators?.[userOperator]?.ppv?.enable;

                    // If PPV is not enabled for user operator, hide the channel
                    if (!ppvEnabled) {
                        return false;
                    }

                    // Check if channel has available events for the current payment method
                    const ppvEvents = getCountryPPVEvents(ppvEventConfig, operators, countryCode);
                    const channelSubscriptionGuids =
                        channel?.subscriptions?.map((sub: any) => sub.subscriptionGUID) || [];

                    const hasAvailableEvent = ppvEvents.some((event: any) =>
                        channelSubscriptionGuids.includes(event.subscriptionGuid)
                    );

                    // Only show PPV channels that have available events
                    return hasAvailableEvent;
                });

                return filteredChannels;
            },
        [ppvEventConfig, operators]
    );

    return {
        // Core detection functions
        detectChannelPPV,
        isPPVItem,
        shouldShowLock,
        getEnhancedChannel,

        // PPV event management
        getPPVEventForChannel,
        isPPVEventPurchased,

        // Channel filtering
        filterChannelsForPPVAccess,

        // Comprehensive status
        getPPVStatus,

        // Configuration state
        ppvConfigLoaded: !!(ppvEventConfig && operators),
        enableLockIcon,
    };
};

export default usePPVManagement;
